import React, { useState } from 'react';
import { Box, Typography, Button, Chip, CircularProgress } from '@material-ui/core';
import { Link as LinkIcon, Check as CheckIcon, CompareArrows as CompareArrowsIcon } from '@material-ui/icons';
import { makeStyles } from '@material-ui/styles';
import { useCampfireQuery } from '../../../../../../global/network/useCampfireQuery';
import { useCampfireTheme } from '../../../../../../theme/useCampfireTheme';
import { FallbackAvatar } from '../../../../../general/user-profile/components/Avatar';
import { GET_MATCHING_CANDIDATES } from './matching-panel-model.gql';

const MATCHING_ROLE_IDS = ['3a957d71-6452-487b-8024-318992f585f4', '7fa5f3df-8002-4c86-aa02-f1d95337766e'];

function getCounterpartRoleId(applicantRoleId: string): string | null {
  if (applicantRoleId === MATCHING_ROLE_IDS[0]) return MATCHING_ROLE_IDS[1];
  if (applicantRoleId === MATCHING_ROLE_IDS[1]) return MATCHING_ROLE_IDS[0];
  return null;
}

function getInitials(name: string): string {
  return name
    .split(' ')
    .map((n) => n[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);
}

function calculateAge(dateOfBirth: string | null | undefined): number | null {
  if (!dateOfBirth) return null;
  const dob = new Date(dateOfBirth);
  const now = new Date();
  let age = now.getFullYear() - dob.getFullYear();
  const monthDiff = now.getMonth() - dob.getMonth();
  if (monthDiff < 0 || (monthDiff === 0 && now.getDate() < dob.getDate())) {
    age--;
  }
  return age;
}

interface MatchingCandidateApplication {
  applicationId: string;
  name: string;
  email: string;
  status: string;
  profile: {
    profileId: string;
    preferredName: string;
    lastName: string;
    dateOfBirth: string | null;
  } | null;
  applicationRoles: Array<{
    applicationRoleId: string;
    role: {
      activityRoleId: string;
      name: string;
    };
    status: string;
  }>;
  funnel: {
    name: string;
  } | null;
}

const useMatchingStyles = () => {
  const { theme } = useCampfireTheme();
  return makeStyles({
    sectionHeading: {
      fontWeight: 600,
      fontSize: 16,
      display: 'flex',
      alignItems: 'center',
      padding: '16px 25px',
      borderBottom: `1px solid ${theme.color.grey.border}`,
    },
    sectionIcon: {
      marginRight: 8,
    },
    subtitle: {
      padding: '10px 25px 12px',
      fontSize: 13,
      color: theme.color.grey.neutral300,
    },
    candidateRow: {
      display: 'flex',
      alignItems: 'center',
      padding: '8px 25px',
      borderBottom: `1px solid ${theme.color.grey.border}`,
      '&:hover': {
        background: theme.palette.grey[100],
      },
    },
    candidateAvatar: {
      marginRight: 16,
    },
    candidateMain: {
      flex: 1,
      minWidth: 0,
    },
    candidateNameRow: {
      display: 'flex',
      alignItems: 'center',
      flexWrap: 'wrap' as const,
    },
    candidateName: {
      fontSize: 14,
      fontWeight: 500,
      marginRight: 8,
    },
    candidateFields: {
      display: 'flex',
      alignItems: 'center',
      marginTop: 6,
      flexWrap: 'wrap' as const,
      fontSize: 13,
      color: theme.color.grey.neutral400,
      '& > span': {
        marginRight: 18,
      },
    },
    fieldLabel: {
      color: theme.color.grey.neutral200,
      marginRight: 4,
    },
    fieldValue: {
      fontWeight: 500,
      color: theme.color.grey.neutralBrand800,
    },
    roleChip: {
      height: 22,
      fontSize: 11,
      fontWeight: 600,
      letterSpacing: 0.3,
      borderRadius: 4,
    },
    matchBtn: {
      textTransform: 'none' as const,
      fontWeight: 500,
      fontSize: 14,
      borderRadius: 6,
      padding: '8px 20px',
    },
    matchedBtn: {
      textTransform: 'none' as const,
      fontWeight: 500,
      fontSize: 14,
      borderRadius: 6,
      padding: '8px 20px',
      backgroundColor: theme.status.green.medium,
      color: theme.palette.primary.contrastText,
      '&:hover': {
        backgroundColor: theme.status.green.dark,
      },
    },
  })();
};

export interface MatchedCandidate {
  applicationId: string;
  name: string;
  profileId: string;
  preferredName: string;
  lastName: string;
  roleName: string;
  roleId: string;
}

interface MatchingPanelProps {
  applicantName: string;
  applicantRoleId: string;
  applicantRoleName: string;
  onMatchSelect?: (candidate: MatchedCandidate | null) => void;
}

export const MatchingPanel: React.FC<MatchingPanelProps> = ({
  applicantName,
  applicantRoleId,
  applicantRoleName,
  onMatchSelect,
}) => {
  const classes = useMatchingStyles();
  const { theme } = useCampfireTheme();
  const [matchedId, setMatchedId] = useState<string | null>(null);

  const counterpartRoleId = getCounterpartRoleId(applicantRoleId);

  const { data, loading } = useCampfireQuery<any, any>(GET_MATCHING_CANDIDATES, {
    options: {
      variables: {
        roleId: counterpartRoleId ?? '',
      },
      skip: !counterpartRoleId,
    },
  });

  const candidates: MatchingCandidateApplication[] = data?.vm?.matchingCandidates ?? [];

  const handleMatch = (candidate: MatchingCandidateApplication) => {
    const isAlreadyMatched = matchedId === candidate.applicationId;
    const newId = isAlreadyMatched ? null : candidate.applicationId;
    setMatchedId(newId);

    if (onMatchSelect) {
      if (isAlreadyMatched || !candidate.profile) {
        onMatchSelect(null);
      } else {
        const roleName =
          candidate.applicationRoles.find((ar) => ar.role.activityRoleId === counterpartRoleId)?.role.name ??
          'Applicant';
        onMatchSelect({
          applicationId: candidate.applicationId,
          name: `${candidate.profile.preferredName} ${candidate.profile.lastName}`,
          profileId: candidate.profile.profileId,
          preferredName: candidate.profile.preferredName,
          lastName: candidate.profile.lastName,
          roleName,
          roleId: counterpartRoleId ?? '',
        });
      }
    }
  };

  if (loading) {
    return (
      <Box padding={4} display='flex' justifyContent='center'>
        <CircularProgress size={24} />
      </Box>
    );
  }

  if (!counterpartRoleId || candidates.length === 0) {
    return null;
  }

  return (
    <Box>
      <Box className={classes.sectionHeading}>
        <CompareArrowsIcon
          className={classes.sectionIcon}
          style={{ color: theme.palette.primary.main, fontSize: 22 }}
        />
        <span>Find a Match</span>
      </Box>

      <Typography className={classes.subtitle}>
        Showing candidates compatible with{' '}
        <strong style={{ color: theme.color.grey.neutralBrand800 }}>{applicantName}</strong> ({applicantRoleName}).
        {` ${candidates.length} candidate${candidates.length !== 1 ? 's' : ''} found.`}
      </Typography>

      <Box>
        {candidates.map((candidate) => {
          const isMatched = matchedId === candidate.applicationId;
          const age = calculateAge(candidate.profile?.dateOfBirth);
          const displayName = candidate.profile
            ? `${candidate.profile.preferredName} ${candidate.profile.lastName}`
            : candidate.name;
          const roleName =
            candidate.applicationRoles.find((ar) => ar.role.activityRoleId === counterpartRoleId)?.role.name ??
            'Applicant';

          return (
            <Box key={candidate.applicationId} className={classes.candidateRow}>
              <Box className={classes.candidateAvatar}>
                <FallbackAvatar initials={getInitials(displayName)} size={40} />
              </Box>

              <Box className={classes.candidateMain}>
                <Box className={classes.candidateNameRow}>
                  <Typography className={classes.candidateName}>{displayName}</Typography>
                  <Chip
                    label={roleName.toUpperCase()}
                    size='small'
                    className={classes.roleChip}
                    style={{
                      backgroundColor: theme.color.grey.neutralBrand100,
                      color: theme.color.grey.neutral400,
                      marginRight: 8,
                    }}
                  />
                  {candidate.funnel && (
                    <Chip
                      label={candidate.funnel.name}
                      size='small'
                      variant='outlined'
                      className={classes.roleChip}
                      style={{ color: theme.color.grey.neutral400 }}
                    />
                  )}
                </Box>

                <Box className={classes.candidateFields}>
                  {age !== null && (
                    <span>
                      <span className={classes.fieldLabel}>Age:</span>
                      <span className={classes.fieldValue}>{age}</span>
                    </span>
                  )}
                  <span>
                    <span className={classes.fieldLabel}>Email:</span>
                    <span className={classes.fieldValue}>{candidate.email}</span>
                  </span>
                </Box>
              </Box>

              <Box flexShrink={0}>
                {isMatched ? (
                  <Button
                    variant='contained'
                    className={classes.matchedBtn}
                    disableElevation
                    startIcon={<CheckIcon style={{ fontSize: 16 }} />}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleMatch(candidate);
                    }}
                  >
                    Matched
                  </Button>
                ) : (
                  <Button
                    variant='contained'
                    color='primary'
                    className={classes.matchBtn}
                    disableElevation
                    startIcon={<LinkIcon style={{ fontSize: 16 }} />}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleMatch(candidate);
                    }}
                  >
                    Match
                  </Button>
                )}
              </Box>
            </Box>
          );
        })}
      </Box>
    </Box>
  );
};
